import {useFormik} from 'formik';
import {useState} from 'react';
import {useLocation} from 'react-router-dom';
import * as Yup from 'yup';
import {AlertProps} from '../../../../../common/components/alerts/Types';
import {HttpError} from '../../../../../common/hooks/ApiClient';
import {useMutation} from '../../../../../common/hooks/QueryHooks';
import {LoggedInEnterprise} from '../../../common/Types';
import {useAuth} from '../../../providers/AuthProvider';
import {useI18n} from '../../../../../common/providers/I18nProvider';
import {getInputValueByName} from '../../../../../common/helpers';

const useLogin = () => {
	const [loading, setLoading] = useState(false);
	const {login, logout} = useAuth();
	const {mutate} = useMutation<LoggedInEnterprise>('/enterpriser/auth/authenticate');
	const location = useLocation();

	const alert = location.state ? (location.state as AlertProps) : undefined;
	const {i18n} = useI18n();

	const loginSchema = Yup.object().shape({
		email: Yup.string()
			.email(i18n('AUTH.LOGIN.VALIDATION.EMAIL.FORMAT'))
			.min(3, i18n('AUTH.LOGIN.VALIDATION.EMAIL.MIN', {val: 3}))
			.max(50, i18n('AUTH.LOGIN.VALIDATION.EMAIL.MAX', {val: 50}))
			.required(i18n('AUTH.LOGIN.VALIDATION.EMAIL.REQUIRED')),
		password: Yup.string()
			.min(6, i18n('AUTH.LOGIN.VALIDATION.PASSWORD.MIN', {val: 6}))
			.max(50, i18n('AUTH.LOGIN.VALIDATION.PASSWORD.MAX', {val: 50}))
			.required(i18n('AUTH.LOGIN.VALIDATION.PASSWORD.REQUIRED')),
	});

	const formik = useFormik({
		initialValues: {
			email: undefined,
			password: undefined,
		},
		validationSchema: loginSchema,
		onSubmit: async (values, {setStatus, setSubmitting}) => {
			setLoading(true);
			try {
				const data = {...values};
				const res = await mutate({data});
				login(res);
			} catch (error) {
				if (error instanceof HttpError) setStatus(error.message);
				logout();
				setSubmitting(false);
			} finally {
				setLoading(false);
			}
		},
	});

	const isEmailInvalid = formik.touched.email && formik.errors.email !== undefined;
	const isPasswordInvalid =
		formik.touched.password !== undefined && formik.touched.password && formik.errors.password !== undefined;

	const hasTouchedEmail = () => {
		if (formik.touched.email !== undefined) return formik.touched.email;

		const autofilled = getInputValueByName('email');
		return autofilled !== undefined && autofilled !== '';
	};

	const canNotSubmit = formik.isSubmitting || !formik.isValid || !hasTouchedEmail();

	return {loading, formik, canNotSubmit, isEmailInvalid, isPasswordInvalid, alert};
};

export default useLogin;
